import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const Report = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null); // 'csv' or 'pdf'
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState({
    employeeId: 'all',
    timePeriod: 'thisMonth',
    startDate: '',
    endDate: ''
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState('');
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');

  // Check if user is admin or has admin-designated role from env
  const isAdminLike = () => {
    const role = localStorage.getItem('role') || '';
    const designationName = localStorage.getItem('designationName') || '';
    const normalizedDesignation = designationName.replace(/\u00A0/g, ' ').trim();
    const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION || '';
    return role === 'admin' || (adminDesignation && normalizedDesignation === adminDesignation.trim());
  };

  useEffect(() => {
    const currentUserId = localStorage.getItem('id') || '';
    
    // If user is not admin-like, set their own employee ID
    if (!isAdminLike() && currentUserId) {
      setFilters(prev => ({
        ...prev,
        employeeId: currentUserId
      }));
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch data when filters or pagination changes
  useEffect(() => {
    // Only fetch if we have valid filters
    if (filters.timePeriod === 'custom') {
      if (filters.startDate && filters.endDate) {
        fetchReportData();
      }
    } else {
      // For non-custom time periods, fetch immediately
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.employeeId, filters.timePeriod, filters.startDate, filters.endDate, pagination.currentPage, pagination.limit]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    if (!showExportDropdown) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      const btnGroup = target.closest('.btn-group');
      const exportDropdown = target.closest('.export-dropdown');
      const exportButton = target.closest('button.employee-back-btn');
      
      // Don't close if clicking inside the button group or dropdown
      if (btnGroup || exportDropdown || exportButton) {
        return;
      }
      
      // Close the dropdown
      setShowExportDropdown(false);
    };

    // Add event listener after a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showExportDropdown]);

  const fetchEmployees = async () => {
    if (!API_URL) return;

    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        return;
      }

      const payload = {
        action: 'get-employees',
        page: 1,
        limit: 1000
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        const employeeList = result.employees || [];
        // Only show employee dropdown for admin-like users (admin or Managing Director)
        if (isAdminLike()) {
          setEmployees(employeeList);
        } else {
          // For non-admin users, don't show employee dropdown
          setEmployees([]);
        }
      }
    } catch (error) {
      // Silently fail - employees dropdown is optional
      console.error('Error fetching employees:', error);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Convert DD-MM-YYYY to YYYY-MM-DD for date input
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    return dateString;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      // Convert YYYY-MM-DD to DD-MM-YYYY for display
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3 && parts[0].length === 4) {
          // Validate date parts
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
          }
        }
      }
      return dateString;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // Auto-set date range based on time period
    if (field === 'timePeriod') {
      const today = new Date();
      let startDate = '';
      let endDate = '';

      switch (value) {
        case 'today':
          startDate = endDate = today.toISOString().split('T')[0];
          break;
        case 'thisWeek':
          // Monday to Sunday of current week
          const weekStart = new Date(today);
          const day = weekStart.getDay();
          const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
          weekStart.setDate(diff);
          startDate = weekStart.toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        case 'thisMonth':
          // First to last day of current month
          startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        case 'lastMonth':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          startDate = lastMonth.toISOString().split('T')[0];
          endDate = lastMonthEnd.toISOString().split('T')[0];
          break;
        case 'thisYear':
          // January 1 to December 31 of current year
          startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
          endDate = today.toISOString().split('T')[0];
          break;
        default:
          // Custom - keep existing dates
          break;
      }

      if (startDate && endDate) {
        setFilters(prev => ({
          ...prev,
          startDate: formatDateForInput(startDate),
          endDate: formatDateForInput(endDate)
        }));
      }
    }
  };

  const mapTimePeriodToAPI = (timePeriod) => {
    const mapping = {
      'today': 'custom',
      'thisWeek': 'this-week',
      'thisMonth': 'this-month',
      'lastMonth': 'custom',
      'thisYear': 'this-year',
      'custom': 'custom'
    };
    return mapping[timePeriod] || 'custom';
  };

  const fetchReportData = async () => {
    // Validate date range for custom time period
    if (filters.timePeriod === 'custom') {
      if (!filters.startDate || !filters.endDate) {
        // Don't fetch if dates are not set for custom period
        return;
      }
      // Validate that start date is before end date
      const start = new Date(filters.startDate);
      const end = new Date(filters.endDate);
      if (start > end) {
        toast.error('Start date must be before or equal to end date');
        return;
      }
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const apiTimePeriod = mapTimePeriodToAPI(filters.timePeriod);
      
      // Build payload safely
      const payload = {
        action: 'get-checkin-checkout-summary',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit,
        timePeriod: apiTimePeriod
      };
      
      // Only include employeeId if user is admin-like and a specific employee is selected
      if (isAdminLike() && filters.employeeId !== 'all') {
        payload.employeeId = filters.employeeId;
      }
      
      // Add date range for custom time period
      if (apiTimePeriod === 'custom') {
        const startDate = formatDateForDisplay(filters.startDate);
        const endDate = formatDateForDisplay(filters.endDate);
        if (startDate && endDate) {
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
      }

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setReportData(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.currentPage || prev.currentPage,
          totalPages: result.totalPages || 1,
          total: result.total || 0,
          hasNextPage: (result.currentPage || prev.currentPage) < (result.totalPages || 1),
          hasPreviousPage: (result.currentPage || prev.currentPage) > 1
        }));
        if (result.data && result.data.length === 0) {
          toast.info('No records found for the selected filters.');
        }
      } else {
        toast.error(result?.message || 'Failed to fetch report data');
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          totalPages: 0,
          total: 0,
          hasNextPage: false,
          hasPreviousPage: false
        }));
      }
    } catch (error) {
      // Safely extract error message without circular reference issues
      let errorMessage = 'Something went wrong. Please try again.';
      try {
        if (error?.response?.data?.message) {
          errorMessage = String(error.response.data.message);
        } else if (error?.message) {
          errorMessage = String(error.message);
        }
        // Only log error message, not the entire error object to avoid circular structure issues
        console.error('Error fetching report data:', errorMessage);
      } catch (e) {
        // If there's still an issue, use default message
        errorMessage = 'Failed to fetch report data. Please try again.';
        console.error('Error parsing error message');
      }
      toast.error(errorMessage);
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 0,
        total: 0,
        hasNextPage: false,
        hasPreviousPage: false
      }));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const getPaginationItems = () => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage;

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const addPage = (page) => {
      if (!items.includes(page)) {
        items.push(page);
      }
    };
    const addEllipsis = () => {
      if (items[items.length - 1] !== 'ellipsis') {
        items.push('ellipsis');
      }
    };

    if (currentPage <= 3) {
      for (let page = 1; page <= Math.min(5, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 2, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else if (currentPage >= totalPages - 2) {
      for (let page = 1; page <= Math.min(3, totalPages); page += 1) {
        addPage(page);
      }
      if (totalPages > 8) {
        addEllipsis();
      }
      for (let page = Math.max(totalPages - 4, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    } else {
      for (let page = Math.max(currentPage - 2, 1); page <= Math.min(currentPage + 2, totalPages); page += 1) {
        addPage(page);
      }
      addEllipsis();
      for (let page = Math.max(totalPages - 3, 1); page <= totalPages; page += 1) {
        addPage(page);
      }
    }

    return items;
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString || timeString === '-') return '-';
    try {
      // Handle format like "14:26:00" (HH:MM:SS)
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'pm' : 'am';
        const displayHour = hour % 12 || 12;
        const formattedHour = String(displayHour).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        return `${formattedHour}:${formattedMinutes} ${ampm}`;
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  const handleViewNotes = (notes, employeeName) => {
    setSelectedNotes(notes || '-');
    setSelectedEmployeeName(employeeName || 'Employee');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedNotes('');
    setSelectedEmployeeName('');
  };

  const stickyHeaderStyle = {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 3
  };

  const stickyHeaderRowStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    backgroundColor: '#fff'
  };

  // const handleExport = async (format) => {
  //   if (filters.timePeriod === 'custom' && (!filters.startDate || !filters.endDate)) {
  //     toast.error('Please select start date and end date before exporting');
  //     return;
  //   }

  //   if (!API_URL) {
  //     toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
  //     return;
  //   }

  //   setExporting(true);
  //   setExportFormat(format);
  //   setShowExportDropdown(false);
    
  //   try {
  //     const currentUserId = localStorage.getItem('id');
      
  //     if (!currentUserId) {
  //       toast.error('User ID not found. Please login again.');
  //       return;
  //     }

  //     const apiTimePeriod = mapTimePeriodToAPI(filters.timePeriod);
  //     const userRole = localStorage.getItem('role');
      
  //     // Build payload safely
  //     const payload = {
  //       action: 'export-checkin-checkout-summary',
  //       currentUserId: currentUserId,
  //       format: format, // 'csv' or 'pdf'
  //       timePeriod: apiTimePeriod
  //     };
      
  //     // Only include employeeId if user is admin and a specific employee is selected
  //     if (userRole === 'admin' && filters.employeeId !== 'all') {
  //       payload.employeeId = filters.employeeId;
  //     }
      
  //     // Add date range for custom time period
  //     if (apiTimePeriod === 'custom') {
  //       const startDate = formatDateForDisplay(filters.startDate);
  //       const endDate = formatDateForDisplay(filters.endDate);
  //       if (startDate && endDate) {
  //         payload.startDate = startDate;
  //         payload.endDate = endDate;
  //       }
  //     }

  //     const response = await axios.post(API_URL, payload, {
  //       headers: {
  //         'Content-Type': 'application/json'
  //       },
  //       responseType: 'blob', // Important for binary file download
  //       // Ensure axios doesn't try to parse the response
  //       transformResponse: [(data) => data]
  //     });

  //     // Check Content-Type from response headers first
  //     const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
  //     console.log('Response Content-Type:', contentType);
  //     console.log('Response data type:', typeof response.data);
  //     console.log('Response data constructor:', response.data?.constructor?.name);
      
  //     // Check if response.data is already a Blob
  //     let blob;
  //     if (response.data instanceof Blob) {
  //       console.log('Response is already a Blob, size:', response.data.size, 'bytes');
  //       blob = response.data;
  //     } else if (response.data instanceof ArrayBuffer) {
  //       console.log('Response is ArrayBuffer, converting to Blob');
  //       blob = new Blob([response.data]);
  //     } else if (typeof response.data === 'string') {
  //       // If API Gateway decoded base64, it might come as a string
  //       // We need to check if it's an error message or actual binary data
  //       console.log('Response is string, length:', response.data.length);
        
  //       // Check if it's a JSON error message
  //       if (response.data.trim().startsWith('{') || response.data.trim().startsWith('[')) {
  //         try {
  //           const errorData = JSON.parse(response.data);
  //           const errorMessage = errorData.message || errorData.error || 'Failed to export. Backend returned an error.';
  //           toast.error(errorMessage);
  //           console.error('Backend error response:', errorData);
  //           return;
  //         } catch (e) {
  //           // Not JSON, might be base64 string that needs decoding
  //           console.warn('String response is not JSON, might need base64 decoding');
  //         }
  //       }
        
  //       // If it's a string, try to convert to binary
  //       // For binary data that came as string (after API Gateway base64 decode), convert to Uint8Array
  //       const binaryString = response.data;
  //       const bytes = new Uint8Array(binaryString.length);
  //       for (let i = 0; i < binaryString.length; i++) {
  //         bytes[i] = binaryString.charCodeAt(i);
  //       }
  //       blob = new Blob([bytes]);
  //       console.log('Converted string to Blob, size:', blob.size, 'bytes');
  //     } else {
  //       // Fallback: try to create blob from whatever we got
  //       console.log('Unknown response type, attempting to create Blob');
  //       blob = new Blob([response.data]);
  //     }
      
  //     // Validate blob size
  //     if (!blob || blob.size === 0) {
  //       toast.error('Received an empty file from the server. Please check backend implementation.');
  //       console.error('Blob is empty or invalid');
  //       return;
  //     }
      
  //     console.log('Final blob size:', blob.size, 'bytes');
  //     console.log('Final blob type:', blob.type);
      
  //     // Check if backend returned an error (JSON response instead of file)
  //     if (contentType.includes('application/json') || blob.size < 100) {
  //       // Backend likely returned an error as JSON
  //       const reader = new FileReader();
  //       const errorText = await new Promise((resolve) => {
  //         reader.onload = () => resolve(reader.result);
  //         reader.readAsText(blob);
  //       });
        
  //       try {
  //         const errorData = JSON.parse(errorText);
  //         const errorMessage = errorData.message || errorData.error || 'Failed to export. Backend returned an error.';
  //         toast.error(errorMessage);
  //         console.error('Backend error response:', errorData);
  //         return;
  //       } catch (e) {
  //         // Not JSON, but file is too small - likely corrupted
  //         if (blob.size < 100) {
  //           toast.error('Received an empty or corrupted file from the server. Please check backend implementation.');
  //           console.error('File too small, likely corrupted. Size:', blob.size);
  //           return;
  //         }
  //       }
  //     }
      
  //     // Validate PDF file size (should be at least a few KB for a valid PDF)
  //     if (format === 'pdf' && blob.size < 1024) {
  //       toast.error('PDF file appears to be corrupted or empty. Please check backend implementation.');
  //       console.error('PDF blob size is too small:', blob.size, 'bytes');
  //       return;
  //     }
      
  //     // Check if backend sent correct content type
  //     if (format === 'pdf') {
  //       if (!contentType.includes('pdf') && !contentType.includes('application/octet-stream') && contentType !== '') {
  //         console.warn('⚠️ Backend returned unexpected Content-Type for PDF:', contentType);
  //         console.warn('Expected: application/pdf or application/octet-stream');
  //         // Don't show warning to user, just log it
  //       }
  //     }
      
  //     if (format === 'csv') {
  //       if (!contentType.includes('csv') && !contentType.includes('text/plain') && !contentType.includes('application/octet-stream') && contentType !== '') {
  //         console.warn('⚠️ Backend returned unexpected Content-Type for CSV:', contentType);
  //       }
  //     }

  //     // Determine MIME type and file extension based on format
  //     // Use the blob's type if available, otherwise use our default
  //     const mimeType = blob.type || (format === 'pdf' ? 'application/pdf' : 'text/csv');
  //     const fileExtension = format === 'pdf' ? 'pdf' : 'csv';
      
  //     // Ensure blob has correct MIME type
  //     if (!blob.type || blob.type === 'application/octet-stream') {
  //       blob = new Blob([blob], { type: mimeType });
  //     }
      
  //     // Validate PDF by checking first few bytes (PDF files start with %PDF)
  //     if (format === 'pdf') {
  //       const firstBytes = await new Promise((resolve) => {
  //         const reader = new FileReader();
  //         reader.onload = () => {
  //           const arrayBuffer = reader.result;
  //           const uint8Array = new Uint8Array(arrayBuffer);
  //           resolve(uint8Array.slice(0, 4));
  //         };
  //         reader.readAsArrayBuffer(blob.slice(0, 4));
  //       });
        
  //       const pdfHeader = String.fromCharCode(...firstBytes);
  //       if (!pdfHeader.startsWith('%PDF')) {
  //         console.error('⚠️ PDF file does not have valid PDF header. First bytes:', pdfHeader);
  //         toast.error('PDF file appears to be corrupted. Please check backend implementation.');
  //         return;
  //       }
  //       console.log('✓ PDF file has valid header:', pdfHeader);
  //     }
      
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = url;
      
  //     // Generate filename with timestamp
  //     const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  //     link.download = `checkin-checkout-summary-${timestamp}.${fileExtension}`;
      
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);
      
  //     toast.success(`${format.toUpperCase()} file downloaded successfully`);
  //   } catch (error) {
  //     console.error('Error exporting report data:', error);
  //     // Safely extract error message without circular reference issues
  //     let errorMessage = 'Failed to export report data. Please try again.';
  //     try {
  //       if (error?.response?.data?.message) {
  //         errorMessage = String(error.response.data.message);
  //       } else if (error?.message) {
  //         errorMessage = String(error.message);
  //       }
  //     } catch (e) {
  //       // If there's still an issue, use default message
  //       errorMessage = 'Failed to export report data. Please try again.';
  //     }
  //     toast.error(errorMessage);
  //   } finally {
  //     setExporting(false);
  //     setExportFormat(null);
  //   }
  // };



  const handleExport = async (format) => {
    if (filters.timePeriod === 'custom' && (!filters.startDate || !filters.endDate)) {
      toast.error('Please select start date and end date before exporting');
      return;
    }
  
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }
  
    setExporting(true);
    setExportFormat(format);
    setShowExportDropdown(false);
  
    try {
      const currentUserId = localStorage.getItem('id');
  
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }
  
      const apiTimePeriod = mapTimePeriodToAPI(filters.timePeriod);
  
      // Build payload
      const payload = {
        action: 'export-checkin-checkout-summary',
        currentUserId: currentUserId,
        format: format, // 'csv' or 'pdf'
        timePeriod: apiTimePeriod
      };
  
      // Only include employeeId if user is admin-like and a specific employee is selected
      if (isAdminLike() && filters.employeeId !== 'all') {
        payload.employeeId = filters.employeeId;
      }
  
      // Add date range for custom time period
      if (apiTimePeriod === 'custom') {
        const startDate = formatDateForDisplay(filters.startDate);
        const endDate = formatDateForDisplay(filters.endDate);
        if (startDate && endDate) {
          payload.startDate = startDate;
          payload.endDate = endDate;
        }
      }
  
      // Make request with blob responseType
      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // CRITICAL: This tells axios to handle response as binary
      });
  
      console.log('Response status:', response.status);
      console.log('Response Content-Type:', response.headers['content-type'] || response.headers['Content-Type']);
      console.log('Response data type:', typeof response.data);
      console.log('Response data constructor:', response.data?.constructor?.name);
  
      // API Gateway should have already decoded base64, but if it didn't, we need to handle it
      let blob = response.data;
  
      // Validate that we got a Blob
      if (!(blob instanceof Blob)) {
        console.error('Response is not a Blob:', typeof response.data);
        
        // If it's a string, it might be an error message
        if (typeof response.data === 'string') {
          try {
            const errorData = JSON.parse(response.data);
            toast.error(errorData.message || errorData.error || 'Failed to export');
            return;
          } catch (e) {
            // Not JSON, might be corrupted data
            console.error('Response is string but not JSON, might be corrupted');
            toast.error('Received invalid response from server');
            return;
          }
        }
        
        // Try to create blob from whatever we got
        blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
        });
      }
  
      console.log('Blob size:', blob.size, 'bytes');
      console.log('Blob type:', blob.type);
  
      // CRITICAL FIX: Check if API Gateway didn't decode base64
      // If blob size matches base64 length (~1.33x original) and header is wrong, decode it
      if (format === 'pdf') {
        const firstChunk = blob.slice(0, 4);
        const arrayBuffer = await firstChunk.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const header = String.fromCharCode(...bytes);
        
        console.log('First 4 bytes (header check):', header);
        
        // If header is not %PDF, API Gateway might not have decoded base64
        // Check if it looks like base64 (starts with letters/numbers, not %PDF)
        if (header !== '%PDF' && /^[A-Za-z0-9+/]/.test(header)) {
          console.warn('⚠️ PDF header is not %PDF, API Gateway may not have decoded base64');
          console.warn('Attempting to decode base64 on frontend...');
          
          // Read the entire blob as text (it's base64)
          const base64String = await blob.text();
          
          // Validate it's base64
          if (!/^[A-Za-z0-9+/=]+$/.test(base64String.trim())) {
            console.error('Blob content is not valid base64');
            toast.error('Received invalid data from server');
            return;
          }
          
          // Decode base64 to binary
          try {
            const binaryString = atob(base64String.trim());
            const binaryBytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              binaryBytes[i] = binaryString.charCodeAt(i);
            }
            
            // Create new blob from decoded binary
            blob = new Blob([binaryBytes], { type: 'application/pdf' });
            console.log('✓ Base64 decoded successfully. New blob size:', blob.size, 'bytes');
            
            // Verify the decoded blob has correct PDF header
            const decodedHeader = String.fromCharCode(...new Uint8Array(await blob.slice(0, 4).arrayBuffer()));
            if (decodedHeader !== '%PDF') {
              console.error('Decoded blob still has invalid header:', decodedHeader);
              toast.error('Failed to decode PDF. Please check API Gateway configuration.');
              return;
            }
            console.log('✓ Decoded PDF has valid header:', decodedHeader);
          } catch (decodeError) {
            console.error('Failed to decode base64:', decodeError);
            toast.error('Failed to decode PDF data');
            return;
          }
        }
      }
  
      // Validate blob is not empty
      if (!blob || blob.size === 0) {
        toast.error('Received an empty file from the server');
        console.error('Blob is empty');
        return;
      }
  
      // Check if it's an error response (JSON wrapped in blob)
      if (blob.type === 'application/json' || blob.size < 100) {
        const text = await blob.text();
        try {
          const errorData = JSON.parse(text);
          toast.error(errorData.message || errorData.error || 'Failed to export');
          console.error('Backend error:', errorData);
          return;
        } catch (e) {
          // Not JSON, but file is suspiciously small
          if (blob.size < 100) {
            toast.error('Received an empty or corrupted file');
            return;
          }
        }
      }
  
      // For PDF, validate the header (if not already validated above)
      if (format === 'pdf') {
        // Ensure blob has correct MIME type
        if (!blob.type || blob.type === 'application/octet-stream') {
          blob = new Blob([blob], { type: 'application/pdf' });
        }
  
        // Validate PDF header (first 4 bytes should be %PDF)
        const firstChunk = blob.slice(0, 4);
        const arrayBuffer = await firstChunk.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const header = String.fromCharCode(...bytes);
  
        if (header !== '%PDF') {
          console.error('Invalid PDF header:', header);
          console.error('First 20 bytes (hex):', Array.from(bytes.slice(0, 20)).map(b => b.toString(16).padStart(2, '0')).join(' '));
          console.error('First 20 bytes (ascii):', String.fromCharCode(...bytes.slice(0, 20)));
          
          // Try to read as text to see if it's an error message
          const fullText = await blob.text();
          if (fullText.trim().startsWith('{') || fullText.trim().startsWith('[')) {
            try {
              const errorData = JSON.parse(fullText);
              toast.error(errorData.message || errorData.error || 'Failed to export PDF');
              return;
            } catch (e) {
              // Not JSON
            }
          }
          
          toast.error('PDF file appears to be corrupted. Header: ' + header);
          return;
        }
  
        console.log('✓ PDF header validated:', header);
      }
  
      // For CSV, ensure correct MIME type
      if (format === 'csv') {
        if (!blob.type || blob.type === 'application/octet-stream') {
          blob = new Blob([blob], { type: 'text/csv' });
        }
      }
  
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `checkin-checkout-summary-${timestamp}.${format}`;
      
      // Append to body, click, then remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
  
      toast.success(`${format.toUpperCase()} file downloaded successfully`);
  
    } catch (error) {
      console.error('Error exporting report:', error);
      
      let errorMessage = 'Failed to export report. Please try again.';
      
      // Handle axios errors
      if (error.response) {
        // If response is a blob, try to read it as text
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (e) {
            // Not JSON, use default message
          }
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setExporting(false);
      setExportFormat(null);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Export Button */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Check In/Check Out Summary
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View daily attendance records including check-ins, check-outs, leaves, and holidays.
              </p>
            </div>
            <div className="col-md-4 employee-view-header-actions">
              <div className="btn-group" style={{ position: 'relative', zIndex: 1001 }}>
                <button
                  type="button"
                  className="employee-back-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!exporting) {
                      setShowExportDropdown(prev => !prev);
                    }
                  }}
                  disabled={exporting}
                  style={{ position: 'relative', zIndex: 1001 }}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Exporting {exportFormat ? `(${exportFormat.toUpperCase()})` : ''}...
                    </>
                  ) : (
                    <>
                      Export
                      <i className={`fas fa-chevron-${showExportDropdown ? 'up' : 'down'} ml-2`}></i>
                    </>
                  )}
                </button>
                {showExportDropdown && !exporting && (
                  <div 
                    className="export-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '5px',
                      backgroundColor: '#fff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 1002,
                      minWidth: '150px',
                      display: 'block',
                      visibility: 'visible'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (reportData.length > 0) {
                          handleExport('csv');
                        } else {
                          toast.warning('No data available to export. Please apply filters first.');
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: reportData.length === 0 ? '#9ca3af' : '#374151',
                        fontSize: '14px',
                        cursor: reportData.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: reportData.length === 0 ? 0.5 : 1,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (reportData.length > 0) {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <i className="fas fa-file-csv mr-2" style={{ color: '#10b981' }}></i>
                      Export as CSV
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (reportData.length > 0) {
                          handleExport('pdf');
                        } else {
                          toast.warning('No data available to export. Please apply filters first.');
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '10px 16px',
                        textAlign: 'left',
                        border: 'none',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: 'transparent',
                        color: reportData.length === 0 ? '#9ca3af' : '#374151',
                        fontSize: '14px',
                        cursor: reportData.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: reportData.length === 0 ? 0.5 : 1,
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (reportData.length > 0) {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <i className="fas fa-file-pdf mr-2" style={{ color: '#ef4444' }}></i>
                      Export as PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  <div className="row" style={{ gap: '20px 0' }}>
                    {/* Select Employee - Only show for admin-like users (admin or Managing Director) */}
                    {isAdminLike() && (
                      <div className="col-md-3">
                        <label htmlFor="employee" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          <i className="fas fa-info-circle mr-1" style={{ fontSize: '11px', color: '#6b7280' }}></i>
                          Select Employee
                        </label>
                        <select
                          id="employee"
                          className="form-control"
                          value={filters.employeeId}
                          onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                          style={{ padding: '8px 12px', fontSize: '14px', height: '40px' }}
                        >
                          <option value="all">All Employees</option>
                          {employees.map((emp) => {
                            const empId = emp._id || emp.id;
                            // Prioritize name field (from API response)
                            let empName = emp.name;
                            
                            // Fallback to FirstName + LastName if name doesn't exist
                            if (!empName && emp.FirstName && emp.LastName) {
                              empName = `${emp.FirstName} ${emp.LastName}`.trim();
                            }
                            
                            // Fallback to firstName + lastName if still no name
                            if (!empName && emp.firstName && emp.lastName) {
                              empName = `${emp.firstName} ${emp.lastName}`.trim();
                            }
                            
                            // Final fallback to email or 'Unknown'
                            if (!empName) {
                              empName = emp.email || 'Unknown';
                            }
                            
                            return (
                              <option key={empId} value={empId}>
                                {empName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}

                    {/* Time Period */}
                    <div className={isAdminLike() ? 'col-md-3' : 'col-md-4'}>
                      <label htmlFor="timePeriod" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        Time Period
                      </label>
                      <select
                        id="timePeriod"
                        className="form-control"
                        value={filters.timePeriod}
                        onChange={(e) => handleFilterChange('timePeriod', e.target.value)}
                        style={{ padding: '8px 12px', fontSize: '14px', height: '40px' }}
                      >
                        <option value="custom">Custom</option>
                        <option value="today">Today</option>
                        <option value="thisWeek">This Week</option>
                        <option value="thisMonth">This Month</option>
                        <option value="lastMonth">Last Month</option>
                        <option value="thisYear">This Year</option>
                      </select>
                    </div>

                    {/* Date Range - Only show when Custom is selected */}
                    {filters.timePeriod === 'custom' && (
                      <div className={isAdminLike() ? 'col-md-4' : 'col-md-5'}>
                        <label htmlFor="dateRange" className="form-label" style={{ marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                          <i className="fas fa-info-circle mr-1" style={{ fontSize: '11px', color: '#6b7280' }}></i>
                          Date Range
                        </label>
                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                          <input
                            type="date"
                            id="startDate"
                            className="form-control"
                            value={filters.startDate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validate year is 4 digits
                              if (value) {
                                const year = value.split('-')[0];
                                if (year && year.length === 4) {
                                  handleFilterChange('startDate', value);
                                } else if (year && year.length > 4) {
                                  // If year is more than 4 digits, truncate to 4
                                  const parts = value.split('-');
                                  if (parts[0].length > 4) {
                                    parts[0] = parts[0].substring(0, 4);
                                    handleFilterChange('startDate', parts.join('-'));
                                  }
                                }
                              } else {
                                handleFilterChange('startDate', value);
                              }
                            }}
                            style={{ padding: '8px 12px', fontSize: '14px', height: '40px', flex: 1 }}
                            max="9999-12-31"
                          />
                          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>to</span>
                          <input
                            type="date"
                            id="endDate"
                            className="form-control"
                            value={filters.endDate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validate year is 4 digits
                              if (value) {
                                const year = value.split('-')[0];
                                if (year && year.length === 4) {
                                  handleFilterChange('endDate', value);
                                } else if (year && year.length > 4) {
                                  // If year is more than 4 digits, truncate to 4
                                  const parts = value.split('-');
                                  if (parts[0].length > 4) {
                                    parts[0] = parts[0].substring(0, 4);
                                    handleFilterChange('endDate', parts.join('-'));
                                  }
                                }
                              } else {
                                handleFilterChange('endDate', value);
                              }
                            }}
                            style={{ padding: '8px 12px', fontSize: '14px', height: '40px', flex: 1 }}
                            max="9999-12-31"
                          />
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Tab Section */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card employee-content-card">
                <div className="card-body">
                  {/* Tab Navigation */}
                  <div className="mb-3" style={{ borderBottom: '2px solid #0094a9' }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setActiveTab('summary')}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        backgroundColor: activeTab === 'summary' ? '#0094a9' : 'transparent',
                        color: activeTab === 'summary' ? '#fff' : '#6b7280',
                        border: 'none',
                        borderBottom: activeTab === 'summary' ? '2px solid #0094a9' : '2px solid transparent',
                        borderRadius: '0',
                        marginBottom: '-2px'
                      }}
                    >
                      Summary
                    </button>
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'summary' && (
                    <div className="table-container">
                      {loading ? (
                        <div className="text-center" style={{ padding: '40px' }}>
                          <Loading size={60} />
                        </div>
                      ) : reportData.length === 0 ? (
                        <div className="text-center" style={{ padding: '40px', color: '#6b7280' }}>
                          <i className="fas fa-filter" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.5 }}></i>
                          <p style={{ fontSize: '16px', margin: 0 }}>Please apply filters to view data</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="employee-table">
                            <thead>
                            <tr style={stickyHeaderRowStyle}>
                              <th style={stickyHeaderStyle}>S.No</th>
                              <th style={{ ...stickyHeaderStyle, maxWidth: '100px', whiteSpace: 'nowrap' }}>Employee</th>
                              <th style={{ ...stickyHeaderStyle, maxWidth: '100px', whiteSpace: 'nowrap' }}>Date</th>
                              <th style={stickyHeaderStyle}>Check-in Time</th>
                              <th style={stickyHeaderStyle}>Check-in Type</th>
                              <th style={stickyHeaderStyle}>Notes</th>
                              <th style={stickyHeaderStyle}>Check-out Time</th>
                              <th style={stickyHeaderStyle}>Checked Out Type</th>
                              <th style={stickyHeaderStyle}>Hours</th>
                              <th style={stickyHeaderStyle}>Day</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.map((record, index) => {
                                // For check-in type: use inLocation if available, otherwise use checkInType
                                const checkInTypeDisplay = (record.inLocation && record.inLocation.trim() !== '') 
                                  ? record.inLocation 
                                  : (record.checkInType || '-');
                                
                                // For check-out type: use outLocation if available, otherwise use checkedOutType
                                const checkOutTypeDisplay = (record.outLocation && record.outLocation.trim() !== '') 
                                  ? record.outLocation 
                                  : (record.checkedOutType || '-');
                                
                                return (
                                  <tr key={record.employeeId || index}>
                                    <td>{getSerialNumber(index)}</td>
                                    <td style={{ maxWidth: '140px',  }}>
                                      {record.employeeName || '-'}
                                    </td>
                                    <td style={{ maxWidth: '100px', whiteSpace: 'nowrap' }}>{record.date || '-'}</td>
                                    <td>{formatTimeForDisplay(record.checkInTime, record.date) || '-'}</td>
                                    <td>{checkInTypeDisplay}</td>
                                    <td>
                                      {record.notes && record.notes.trim() ? (
                                        <button
                                          type="button"
                                          className="employee-action-btn employee-action-view"
                                          onClick={() => handleViewNotes(record.notes, record.employeeName)}
                                          title="View notes"
                                          style={{ 
                                            padding: '2px 6px',
                                            minWidth: 'auto',
                                            fontSize: '12px'
                                          }}
                                        >
                                          <i className="fa-solid fa-eye"></i>
                                        </button>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                    <td>{formatTimeForDisplay(record.checkOutTime, record.date) || '-'}</td>
                                    <td>{checkOutTypeDisplay}</td>
                                    <td>{record.hours || '-'}</td>
                                    <td>{record.day || '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {/* Pagination */}
                      {reportData.length > 0 && pagination.totalPages > 0 && (
                        <div className="employee-pagination">
                          <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                            <label htmlFor="reportPaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                              Show:
                            </label>
                            <select
                              id="reportPaginationLimit"
                              value={pagination.limit}
                              onChange={handleLimitChange}
                              style={{
                                padding: '6px 10px',
                                fontSize: '14px',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                minWidth: '70px'
                              }}
                            >
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="75">75</option>
                              <option value="100">100</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(1)}
                              disabled={pagination.currentPage === 1}
                            >
                              «
                            </button>
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                            >
                              ‹
                            </button>
                            {getPaginationItems().map((item, index) => {
                              if (item === 'ellipsis') {
                                return (
                                  <span
                                    key={`ellipsis-${index}`}
                                    className="pagination-btn"
                                    style={{
                                      cursor: 'default',
                                      padding: '6px 10px',
                                      backgroundColor: 'transparent',
                                      borderColor: 'transparent'
                                    }}
                                  >
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={item}
                                  className={`pagination-btn ${pagination.currentPage === item ? 'active' : ''}`}
                                  onClick={() => handlePageChange(item)}
                                >
                                  {item}
                                </button>
                              );
                            })}
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              ›
                            </button>
                            <button
                              className="pagination-btn"
                              onClick={() => handlePageChange(pagination.totalPages)}
                              disabled={pagination.currentPage === pagination.totalPages}
                            >
                              »
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="modal-overlay" onClick={closeNotesModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Notes</h3>
              <button className="modal-close" onClick={closeNotesModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#6b7280' }}>
                <strong>Employee:</strong> {selectedEmployeeName}
              </p>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                minHeight: '60px',
                maxHeight: '300px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                {selectedNotes}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeNotesModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;